import api from './api';
import {
  ConversationLeadStatus,
  getConversationLeadStatusColor,
  getConversationLeadStatusLabel,
} from '../components/Conversations/conversationLeadStatus';

// Types for conversation management
export interface ConversationSummary {
  id: string;
  user_id: string;
  customer_name?: string;
  customer_phone?: string;
  hubspot_contact_id?: string;
  hubspot_deal_id?: string;
  hubspot_deal_project_name?: string | null;
  hubspot_contact_owner_email_at_start?: string;
  perfil_del_cliente?: string;
  resumen_de_conversacion?: string;
  observaciones?: string;
  start_time: string;
  end_time?: string;
  message_count: number;
  lead_score: number;
  lead_status: ConversationLeadStatus;
  loss_reason?: ConversationLossReason | null;
  decline_reason?: DeclineReason | null;
  project_interest?: string;
  ai_enabled?: boolean;
  is_active?: boolean;
  is_archived?: boolean;
  archived_at?: string;
  archived_by?: string;
  delivery_status?: ConversationDeliveryStatus;
  duration_minutes?: number;
  last_activity_at?: string;
  last_message_preview?: string;
}

export type WaDeliveryStatus =
  | 'processing'
  | 'sent'
  | 'delivered'
  | 'read'
  | 'failed'
  | 'retry_scheduled'
  | 'retry_exhausted';

export type ConversationDeliveryStatus =
  | 'pending_delivery'
  | 'active'
  | 'unreachable'
  | 'opted_out';

export type ConversationDeliveryBucket =
  | 'main_inbox'
  | 'pending'
  | 'not_delivered'
  | 'all';

export type ConversationLossReason =
  | 'delivery_failed'
  | 'no_engagement'
  | 'abandoned'
  | 'declined';

export type ConversationLossReasonFilter = ConversationLossReason | 'unclassified';

export type DeclineReason = string;

export interface Message {
  id: string;
  conversation_id: string;
  content: string;
  sender: 'user' | 'bot';
  receiver?: 'user' | 'bot';
  message_type: 'text' | 'audio' | 'image' | 'document';
  message_category?: string;
  reference_message_id?: string;
  reaction?: string;
  confidence_score?: number;
  processing_time?: number;
  audio_duration?: number;
  file_path?: string;
  is_flagged: boolean;
  is_human_takeover?: boolean;
  human_sender_name?: string;
  whatsapp_message_id?: string;
  campaign_id?: string;
  wa_status?: WaDeliveryStatus;
  wa_status_accepted_at?: string;
  wa_status_sent_at?: string;
  wa_status_delivered_at?: string;
  wa_status_read_at?: string;
  wa_status_failed_at?: string;
  wa_error_code?: number;
  wa_error_description?: string;
  retry_count?: number;
  max_retries?: number;
  next_retry_at?: string;
  retry_exhausted?: boolean;
  timestamp: string;
  created_at: string;
}

export interface ConversationPdfExportOptions {
  include_summary: boolean;
  include_transcript: boolean;
}

export interface ProactiveFollowUpJobSummary {
  kind: string;
  status: string;
  due_at?: string;
  reference_ts?: string;
  sent_at?: string;
  attempt_count: number;
  reason?: string;
}

export interface ProactiveFollowUpStatus {
  next_action?: ProactiveFollowUpJobSummary | null;
  last_outcome?: ProactiveFollowUpJobSummary | null;
}

export interface ConversationDetail {
  id: string;
  user_id: string;
  customer_name?: string;
  customer_phone?: string;
  hubspot_contact_id?: string;
  hubspot_deal_id?: string;
  hubspot_deal_project_name?: string | null;
  hubspot_contact_owner_email_at_start?: string;
  profile_snapshot_json?: Record<string, unknown>;
  perfil_del_cliente?: string;
  resumen_de_conversacion?: string;
  observaciones?: string;
  start_time: string;
  end_time?: string;
  message_count: number;
  lead_score: number;
  lead_status: ConversationLeadStatus;
  loss_reason?: ConversationLossReason | null;
  decline_reason?: DeclineReason | null;
  project_interest?: string;
  ai_enabled?: boolean;
  is_active: boolean;
  is_archived?: boolean;
  archived_at?: string;
  archived_by?: string;
  delivery_status?: ConversationDeliveryStatus;
  created_at: string;
  updated_at: string;
  messages: Message[];
  proactive_followup?: ProactiveFollowUpStatus;
  analytics?: {
    total_messages: number;
    user_messages: number;
    bot_messages: number;
    conversation_duration?: number;
    avg_response_time?: number;
    sentiment_score?: number;
    engagement_score?: number;
    topics_discussed?: string;
    projects_mentioned?: string;
  };
}

export interface ConversationFilters {
  user_id?: string;
  lead_status?: ConversationLeadStatus;
  lead_statuses?: ConversationLeadStatus[];
  loss_reason?: ConversationLossReasonFilter;
  decline_reason?: DeclineReason;
  project_interest?: string;
  customer_phone?: string;
  start_date?: string;
  end_date?: string;
  min_lead_score?: number;
  max_lead_score?: number;
  is_active?: boolean;
  archive_status?: ConversationArchiveStatus;
  delivery_bucket?: ConversationDeliveryBucket;
  sort_by?: ConversationSortField;
  sort_direction?: SortDirection;
  limit?: number;
  offset?: number;
}

export interface ConversationSearchQuery {
  query: string;
  filters?: ConversationFilters;
}

export interface ConversationListResponse {
  conversations: ConversationSummary[];
  total_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface ConversationStats {
  total_conversations: number;
  active_conversations: number;
  total_messages: number;
  avg_lead_score?: number;
  average_lead_score?: number;
  lead_status_distribution: Record<string, number>;
  loss_reason_distribution?: Record<string, number>;
  decline_reason_distribution?: Record<string, number>;
  conversations_by_date?: Array<{ date: string; count: number }>;
  messages_today?: number;
}

export interface ConversationAnalytics {
  conversation_id: string;
  total_messages: number;
  user_messages: number;
  bot_messages: number;
  conversation_duration_minutes?: number;
  avg_response_time_seconds?: number;
  message_types_distribution: Record<string, number>;
  audio_messages_count: number;
  total_audio_duration_seconds: number;
  lead_score: number;
  lead_status: string;
  project_interest?: string;
  customer_engagement: {
    messages_per_session: number;
    avg_message_length: number;
    has_audio_messages: boolean;
  };
}

export type SortDirection = 'asc' | 'desc';

export type ConversationArchiveStatus = 'active' | 'archived' | 'all';

export type ConversationSortField =
  | 'customer'
  | 'lead_status'
  | 'lead_score'
  | 'project_interest'
  | 'message_count'
  | 'duration_minutes'
  | 'last_activity_at';

export type ExportFormat = 'csv' | 'json' | 'pdf' | 'excel';

export interface ExportRequest {
  format: ExportFormat;
  query?: string;
  filters?: ConversationFilters;
  include_messages?: boolean;
}

export interface BlobDownload {
  blob: Blob;
  filename?: string;
}

export interface ConversationAIControlResponse {
  conversation_id: string;
  ai_enabled: boolean;
  updated_at: string;
  updated_by: string;
}

export interface ConversationArchiveResponse {
  conversation_id: string;
  is_archived: boolean;
  archived_at?: string;
  archived_by?: string;
  updated_at: string;
  updated_by: string;
}

export interface ManualMessageSendRequest {
  message: string;
  header_text?: string;
  footer_text?: string;
  disable_ai_on_send?: boolean;
}

export interface ManualMessageSendResponse {
  conversation_id: string;
  message_id: string;
  delivered: boolean;
  ai_enabled: boolean;
  ai_auto_disabled: boolean;
  stored_message: Message;
}

export interface ManualMessageRetryResponse {
  conversation_id: string;
  message_id: string;
  wa_status?: WaDeliveryStatus;
  stored_message: Message;
}

export interface ConversationCloseImportRow {
  id: number;
  row_number: number;
  hubspot_deal_id?: string;
  conversation_id?: string;
  lead_status?: string;
  stage_decision?: string;
  status: string;
  error_code?: string;
  error_message?: string;
  warning_message?: string;
  attempt_count: number;
  locally_closed: boolean;
  summary_synced: boolean;
  deal_synced: boolean;
  report_synced: boolean;
}

export interface ConversationCloseImportJob {
  job_id: string;
  source_filename: string;
  status: string;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  processed_rows: number;
  succeeded_rows: number;
  partial_rows: number;
  skipped_rows: number;
  failed_rows: number;
  pending_rows: number;
  progress_percent: number;
  created_at: string;
  queued_at?: string;
  started_at?: string;
  completed_at?: string;
  cancelled_at?: string;
  rows: ConversationCloseImportRow[];
}

export interface ConversationCloseImportRows {
  job_id: string;
  total_count: number;
  skip: number;
  limit: number;
  rows: ConversationCloseImportRow[];
}

export interface ConversationDealReconciliationRow {
  id: number;
  row_number: number;
  phone_number?: string;
  hubspot_deal_id?: string;
  conversation_id?: string;
  lead_status?: string;
  old_project_interest?: string;
  old_hubspot_deal_id?: string;
  live_project_label?: string;
  live_dealstage?: string;
  stage_decision?: string;
  target_dealstage?: string;
  status: string;
  error_code?: string;
  error_message?: string;
  warning_message?: string;
  attempt_count: number;
  hubspot_owner_synced: boolean;
  hubspot_stage_synced: boolean;
  local_binding_updated: boolean;
}

export interface ConversationDealReconciliationJob {
  job_id: string;
  source_filename: string;
  project_code: string;
  project_name: string;
  status: string;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  processed_rows: number;
  succeeded_rows: number;
  partial_rows: number;
  skipped_rows: number;
  failed_rows: number;
  pending_rows: number;
  progress_percent: number;
  created_at: string;
  queued_at?: string;
  started_at?: string;
  completed_at?: string;
  cancelled_at?: string;
  rows: ConversationDealReconciliationRow[];
}

export interface ConversationDealReconciliationRows {
  job_id: string;
  total_count: number;
  skip: number;
  limit: number;
  rows: ConversationDealReconciliationRow[];
}

class ConversationService {
  async getConversations(filters?: ConversationFilters): Promise<ConversationListResponse> {
    const params = new URLSearchParams();
    
    if (filters) {
      (Object.entries as any)(filters).forEach(([key, value]: [string, any]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach((item) => {
              params.append(key, item.toString());
            });
            return;
          }

          params.append(key, value.toString());
        }
      });
    }
    
    const query = params.toString();
    const url = query ? `/conversations/?${query}` : '/conversations/';
    const response = await api.get(url);
    return response.data;
  }

  async searchConversations(searchQuery: ConversationSearchQuery): Promise<ConversationListResponse> {
    const response = await api.post('/conversations/search', searchQuery);
    return response.data;
  }

  async getConversationDetail(conversationId: string): Promise<ConversationDetail> {
    const response = await api.get(`/conversations/${conversationId}`);
    return response.data;
  }

  async setConversationAiControl(
    conversationId: string,
    aiEnabled: boolean
  ): Promise<ConversationAIControlResponse> {
    const response = await api.patch(`/conversations/${conversationId}/ai-control`, {
      ai_enabled: aiEnabled,
    });
    return response.data;
  }

  async setConversationArchived(
    conversationId: string,
    isArchived: boolean
  ): Promise<ConversationArchiveResponse> {
    const response = await api.patch(`/conversations/${conversationId}/archive`, {
      is_archived: isArchived,
    });
    return response.data;
  }

  async sendManualMessage(
    conversationId: string,
    payload: ManualMessageSendRequest
  ): Promise<ManualMessageSendResponse> {
    const response = await api.post(`/conversations/${conversationId}/messages/manual`, payload);
    return response.data;
  }

  async retryManualMessage(
    conversationId: string,
    messageId: string
  ): Promise<ManualMessageRetryResponse> {
    const response = await api.post(`/conversations/${conversationId}/messages/${messageId}/retry`);
    return response.data;
  }

  async getConversationMessages(
    conversationId: string,
    limit: number = 100,
    offset: number = 0
  ): Promise<{ messages: Message[]; total_count: number; has_more: boolean }> {
    const response = await api.get(
      `/conversations/${conversationId}/messages?limit=${limit}&offset=${offset}`
    );
    return response.data;
  }

  async getConversationStats(): Promise<ConversationStats> {
    const response = await api.get('/conversations/stats/overview');
    return response.data.stats;
  }

  async exportConversations(exportRequest: ExportRequest): Promise<Blob> {
    const response = await api.post('/conversations/export', exportRequest, {
      responseType: 'blob'
    });
    return response.data;
  }

  private getFilenameFromContentDisposition(contentDisposition?: string): string | undefined {
    if (!contentDisposition) return undefined;

    const encodedMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (encodedMatch?.[1]) {
      try {
        return decodeURIComponent(encodedMatch[1].replace(/["']/g, '').trim());
      } catch {
        return encodedMatch[1].replace(/["']/g, '').trim();
      }
    }

    const match = contentDisposition.match(/filename="?([^";]+)"?/i);
    return match?.[1]?.trim();
  }

  async exportConversationPdf(
    conversationId: string,
    options: ConversationPdfExportOptions
  ): Promise<BlobDownload> {
    const response = await api.post(`/conversations/${conversationId}/report.pdf`, options, {
      responseType: 'blob'
    });
    return {
      blob: response.data,
      filename: this.getFilenameFromContentDisposition(response.headers['content-disposition']),
    };
  }

  async downloadConversationCloseTemplate(): Promise<BlobDownload> {
    const response = await api.get('/conversations/bulk-close/template.xlsx', {
      responseType: 'blob',
    });
    return {
      blob: response.data,
      filename:
        this.getFilenameFromContentDisposition(response.headers['content-disposition']) ||
        'conversation_close_template.xlsx',
    };
  }

  async validateConversationCloseImport(file: File): Promise<ConversationCloseImportJob> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/conversations/bulk-close/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async queueConversationCloseImport(jobId: string): Promise<ConversationCloseImportJob> {
    const response = await api.post(`/conversations/bulk-close/jobs/${jobId}/queue`);
    return response.data;
  }

  async getConversationCloseImport(jobId: string): Promise<ConversationCloseImportJob> {
    const response = await api.get(`/conversations/bulk-close/jobs/${jobId}`);
    return response.data;
  }

  async getConversationCloseImportRows(
    jobId: string,
    limit: number = 100
  ): Promise<ConversationCloseImportRows> {
    const response = await api.get(
      `/conversations/bulk-close/jobs/${jobId}/rows?limit=${limit}`
    );
    return response.data;
  }

  async retryConversationCloseImport(jobId: string): Promise<ConversationCloseImportJob> {
    const response = await api.post(`/conversations/bulk-close/jobs/${jobId}/retry`);
    return response.data;
  }

  async cancelConversationCloseImport(jobId: string): Promise<ConversationCloseImportJob> {
    const response = await api.post(`/conversations/bulk-close/jobs/${jobId}/cancel`);
    return response.data;
  }

  async downloadConversationCloseResults(jobId: string): Promise<BlobDownload> {
    const response = await api.get(
      `/conversations/bulk-close/jobs/${jobId}/results.xlsx`,
      { responseType: 'blob' }
    );
    return {
      blob: response.data,
      filename:
        this.getFilenameFromContentDisposition(response.headers['content-disposition']) ||
        `conversation_close_results_${jobId}.xlsx`,
    };
  }

  async downloadConversationDealReconciliationTemplate(): Promise<BlobDownload> {
    const response = await api.get('/conversations/bulk-reconcile/template.xlsx', { responseType: 'blob' });
    return {
      blob: response.data,
      filename: this.getFilenameFromContentDisposition(response.headers['content-disposition']) ||
        'conversation_deal_reconciliation_template.xlsx',
    };
  }

  async validateConversationDealReconciliation(
    projectCode: string,
    file: File
  ): Promise<ConversationDealReconciliationJob> {
    const formData = new FormData();
    formData.append('project_interest', projectCode);
    formData.append('file', file);
    const response = await api.post('/conversations/bulk-reconcile/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async queueConversationDealReconciliation(jobId: string): Promise<ConversationDealReconciliationJob> {
    return (await api.post(`/conversations/bulk-reconcile/jobs/${jobId}/queue`)).data;
  }

  async getConversationDealReconciliation(jobId: string): Promise<ConversationDealReconciliationJob> {
    return (await api.get(`/conversations/bulk-reconcile/jobs/${jobId}`)).data;
  }

  async getConversationDealReconciliationRows(
    jobId: string,
    limit: number = 100
  ): Promise<ConversationDealReconciliationRows> {
    return (await api.get(`/conversations/bulk-reconcile/jobs/${jobId}/rows?limit=${limit}`)).data;
  }

  async retryConversationDealReconciliation(jobId: string): Promise<ConversationDealReconciliationJob> {
    return (await api.post(`/conversations/bulk-reconcile/jobs/${jobId}/retry`)).data;
  }

  async cancelConversationDealReconciliation(jobId: string): Promise<ConversationDealReconciliationJob> {
    return (await api.post(`/conversations/bulk-reconcile/jobs/${jobId}/cancel`)).data;
  }

  async downloadConversationDealReconciliationResults(jobId: string): Promise<BlobDownload> {
    const response = await api.get(`/conversations/bulk-reconcile/jobs/${jobId}/results.xlsx`, {
      responseType: 'blob',
    });
    return {
      blob: response.data,
      filename: this.getFilenameFromContentDisposition(response.headers['content-disposition']) ||
        `conversation_deal_reconciliation_results_${jobId}.xlsx`,
    };
  }

  async getConversationAnalytics(conversationId: string): Promise<ConversationAnalytics> {
    const response = await api.get(`/conversations/${conversationId}/analytics`);
    return response.data;
  }

  async getAudioMessage(conversationId: string, messageId: string): Promise<{
    message_id: string;
    conversation_id: string;
    audio_duration?: number;
    file_path?: string;
    download_url?: string;
    message: string;
  }> {
    const response = await api.get(`/conversations/${conversationId}/audio/${messageId}`);
    return response.data;
  }

  // Helper methods for formatting
  formatLeadStatus(status: string): string {
    return getConversationLeadStatusLabel(status);
  }

  getLeadStatusColor(status: string): string {
    return getConversationLeadStatusColor(status);
  }

  formatDuration(minutes?: number): string {
    if (!minutes) return 'N/A';
    
    if (minutes < 60) {
      return `${Math.round(minutes)}m`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    return `${hours}h ${remainingMinutes}m`;
  }

  formatMessageType(type: string): string {
    const typeMap: Record<string, string> = {
      text: 'Texto',
      audio: 'Audio',
      image: 'Imagen',
      document: 'Documento'
    };
    return typeMap[type] || type;
  }
}

export const conversationService = new ConversationService();
export default conversationService;
